import React, { useEffect, useState } from 'react';
import { PiBellDuotone, PiCheckCircleDuotone, PiNotificationDuotone, PiUsersDuotone } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import Button from '../shared/common/Button';
import Card from '../shared/common/Card';
import EmptyState from '../shared/common/EmptyState';
import { useNotifications } from '../app/providers/NotificationContext';
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../api/notificationService';

const ENTITY_TYPE_CHALLENGE = 'CHALLENGE';
const ENTITY_TYPE_FRIEND_REQUEST = 'FRIEND_REQUEST';
const ENTITY_TYPE_ROOM = 'ROOM';
const TYPE_LABELS = {
  ROOM_INVITE: 'Комната',
  CHALLENGE_CREATED: 'Новый спор',
  CHALLENGE_RESOLVED: 'Пари завершено',
  SIDE_BET_CREATED: 'Особый исход',
  FRIEND_REQUEST: 'Друзья',
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { refreshUnreadCount, enablePushNotifications } = useNotifications();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    setError('');
    try {
      setNotifications(await getNotifications());
      await refreshUnreadCount();
    } catch {
      setError('Не удалось загрузить уведомления');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (notification) => {
    if (!notification.unread) return;

    setActionLoading(`read-${notification.id}`);
    setError('');
    try {
      const updated = await markNotificationRead(notification.id);
      setNotifications((items) => items.map((item) => (item.id === updated.id ? updated : item)));
      await refreshUnreadCount();
    } catch {
      setError('Не удалось отметить уведомление прочитанным');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkAllRead = async () => {
    setActionLoading('read-all');
    setError('');
    try {
      await markAllNotificationsRead();
      await loadNotifications();
    } catch {
      setError('Не удалось отметить все уведомления прочитанными');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEnablePush = async () => {
    setActionLoading('enable-push');
    setError('');
    try {
      await enablePushNotifications();
    } catch (pushError) {
      setError(pushError instanceof Error ? pushError.message : 'Не удалось включить push-уведомления');
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpen = async (notification) => {
    await handleMarkRead(notification);
    const targetRoute = notificationRoute(notification);
    if (targetRoute) {
      navigate(targetRoute);
    }
  };

  return (
    <div className="bm-animate-in" style={{ display: 'grid', gap: 'var(--bm-space-lg)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--bm-space-md)', flexWrap: 'wrap' }}>
        <div>
          <h1 className="bm-h2" style={{ marginBottom: 'var(--bm-space-xs)' }}>Уведомления</h1>
Важные события портала: комнаты, споры, особые исходы и друзья.
        </div>
        <div style={{ display: 'flex', gap: 'var(--bm-space-sm)', flexWrap: 'wrap' }}>
          <Button
            variant="primary"
            icon={<PiNotificationDuotone size={16} />}
            loading={actionLoading === 'enable-push'}
            onClick={handleEnablePush}
          >
            Включить push
          </Button>
          <Button
            variant="secondary"
            icon={<PiCheckCircleDuotone size={16} />}
            loading={actionLoading === 'read-all'}
            disabled={!notifications.some((notification) => notification.unread)}
            onClick={handleMarkAllRead}
          >
            Прочитать все
          </Button>
        </div>
      </div>

      {error && <div className="bm-auth-error">{error}</div>}

      {loading ? (
        <p className="bm-muted">Загружаем уведомления…</p>
      ) : notifications.length ? (
        <div style={{ display: 'grid', gap: 'var(--bm-space-sm)' }}>
          {notifications.map((notification) => (
            <NotificationRow
              key={notification.id}
              notification={notification}
              loading={actionLoading === `read-${notification.id}`}
              onOpen={() => handleOpen(notification)}
              onMarkRead={() => handleMarkRead(notification)}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          icon={<PiBellDuotone size={32} />}
          title="Пока нет уведомлений"
hint="Новые споры, особые исходы, приглашения и завершённые пари будут здесь."
        />
      )}
    </div>
  );
}

function NotificationRow({ notification, loading, onOpen, onMarkRead }) {
  return (
    <Card
      variant="interactive"
      className={notification.unread ? 'bm-notification-card bm-notification-card--unread' : 'bm-notification-card'}
      onClick={onOpen}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--bm-space-md)' }}>
        <div style={{ display: 'flex', gap: 'var(--bm-space-sm)' }}>
          <div className="bm-notification-card__icon">
            <PiUsersDuotone size={20} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--bm-space-xs)', flexWrap: 'wrap' }}>
              <span className="bm-notification-card__type">{TYPE_LABELS[notification.type] || 'Уведомление'}</span>
              {notification.unread && <span className="bm-notification-card__unread">Новое</span>}
            </div>
            <p style={{ margin: '6px 0 4px' }}>{notification.message}</p>
            <p className="bm-muted" style={{ margin: 0 }}>{formatDate(notification.createdAt)}</p>
          </div>
        </div>
        {notification.unread && (
          <Button size="sm" variant="ghost" loading={loading} onClick={onMarkRead}>
            Прочитано
          </Button>
        )}
      </div>
    </Card>
  );
}

function notificationRoute(notification) {
  if (notification.entityType === ENTITY_TYPE_ROOM) {
    return `/rooms/${notification.roomId || notification.entityId}/challenges`;
  }
  if (notification.entityType === ENTITY_TYPE_CHALLENGE && notification.roomId) {
    return notification.leagueId
      ? `/rooms/${notification.roomId}/leagues/${notification.leagueId}/challenges/${notification.entityId}`
      : `/rooms/${notification.roomId}/challenges/${notification.entityId}`;
  }
  if (notification.entityType === ENTITY_TYPE_FRIEND_REQUEST) {
    return '/friends';
  }
  return null;
}

function formatDate(value) {
  if (!value) {
    return '';
  }
  return new Intl.DateTimeFormat('ru-RU', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}
